import { useNavigate } from '@tanstack/react-router'

import { useAcceptMyTerms } from '@/api/gen/endpoints/affiliate'
import { MutationError } from '@/components/mutation-error'
import { OnboardingWizard } from '@/components/onboarding-wizard/onboarding-wizard'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { store } from '@/store'

export const AcceptTermsPage = () => {
  const navigate = useNavigate()
  const { hasError, onError, showError, clearError } =
    useMutationErrorHandler('acceptMyTerms failed: ')

  const { mutate: acceptTerms, isPending } = useAcceptMyTerms({
    mutation: {
      onError,
      onSuccess: async () => {
        // The POST already succeeded — terms ARE accepted server-side. The
        // refetch only re-syncs the client profile; its result tells us how to
        // recover without ever re-POSTing an acceptance that already landed.
        const result = await store.getState().refetchPartner()
        if (result.status === 'success') {
          await navigate({ to: '/dashboard' })

          return
        }

        // Session genuinely expired mid-flow (401): the accepted terms persist;
        // send the partner to re-authenticate rather than show a retry that
        // would re-POST. /login replaces the dead-end limbo with a real exit.
        if (result.status === 'unauthenticated') {
          await navigate({ to: '/login', replace: true })

          return
        }

        // Transient transport failure: the acceptance is safe, but we could not
        // re-sync the profile. Show the inline error so the partner can retry —
        // POST /me/terms/ is idempotent (it sets terms_accepted_at and returns
        // the profile), so a retry from the Accept control re-accepts harmlessly.
        // `refetchPartner` is the single observability owner for this failure; use
        // `showError` (UI only) so we don't double-report it under the unrelated
        // 'acceptMyTerms failed: ' prefix.
        showError()
      },
    },
  })

  const handleAccept = () => {
    clearError()
    acceptTerms()
  }

  const errorSlot = (
    <MutationError
      data-tst="accept-terms-error"
      messageId="terms.acceptError"
      show={hasError}
    />
  )

  return (
    <OnboardingWizard
      errorSlot={errorSlot}
      isAccepting={isPending}
      onAccept={handleAccept}
    />
  )
}
